import React, { useState, useRef } from 'react';
import cn from 'classnames';
import { Checkbox, Button } from 'antd';
import ThemePrice from '@base/lib/ThemePrice';
import Iconfont from '@base/lib/Iconfont';
import { ITogetherProduct, IVariant } from 'base/interfaces/product';
import useTogether from '@base/product/useTogether';
import prodListStyles from 'app/styles/modules/prod-list.module.less';
import styles from './index.module.less';
import useProductTagScale from 'base/hooks/useProductTagScale';
import Tracker from '@base/lib/Tracker';
import { isSoldOut } from 'base/utils/product';
import { useTrans } from 'base/utils/i18n';
import Image from '@base/lib/ThemeImage';
import { getMainImage } from 'base/utils/getPicUrl';
import { ProductDetailPopup, IProductDetailPopupProps } from '@app/common/ProductDetailPopup';

type IProps = {
    productId: string;
    shownVariant: IVariant;
}

const MainProductItem: React.FC<{ item: IVariant }> = (props) => {
    const { item } = props
    const {
        shopPriceExchangeDisplay,
        dealPriceExchangeDisplay,
        pics
    } = item

    const { t } = useTrans();

    const isDeal = dealPriceExchangeDisplay;

    const mianPic = getMainImage(pics)

    return (
        <div className={cn(styles['item'])}>
            <div className="flex">
                <Image
                    src={mianPic?.picLink}
                    alt={mianPic?.altText}
                    width={150}
                    height={150}
                    fixed
                />
                <div className={styles.operator}>
                    <Iconfont className="icon-add"/>
                </div>
            </div>
            <div className={styles['info-wrap']}>
                <p className={'g-title'}>
                    <strong>{t('web_product_compare_this_item')}: </strong>
                    {item.name}
                </p>

                <div className="flex flex-justify">
                    <div className={cn('g-prices')}>
                        <div>
                            <strong className={cn('g-shop-price', { 'g-highlight': isDeal })}>
                                {dealPriceExchangeDisplay || shopPriceExchangeDisplay}
                            </strong>
                        </div>

                        {isDeal && (<del className="g-market-price">{shopPriceExchangeDisplay}</del>)}
                    </div>
                    <Checkbox checked disabled={true} className={styles.checkbox} value={item.variantId}/>
                </div>
            </div>
        </div>
    )
}

const TogetherProductItem: React.FC<{
    item: ITogetherProduct,
    showLinkSymbol: boolean;
    onQuickView: (variantId: string) => void;
}> = (props) => {
    const { item, showLinkSymbol, onQuickView } = props
    const {
        shopPriceExchangeDisplay,
        marketPriceExchangeDisplay,
    } = item

    const hasMarketPrice = shopPriceExchangeDisplay !== marketPriceExchangeDisplay;

    return (
        <div className={cn(prodListStyles.item, styles.item,)}>
            <div className="flex">
                <div
                    className={'pointer'}
                    onClick={() => onQuickView(item.variantId)}
                >
                    <Image
                        src={item.pic.url}
                        alt={item.pic.altText || item.title}
                        width={150}
                        height={150}
                        fixed
                    />
                </div>

                <div className={styles.operator}>
                    {showLinkSymbol ? <Iconfont className="icon-add"/> : null}
                </div>
            </div>
            <div className={styles['info-wrap']}>
                <p
                    className={'g-title pointer'}
                    onClick={() => onQuickView(item.variantId)}
                >
                    {item.title}
                </p>

                <div className="flex flex-justify">
                    <div className={cn('g-prices')}>
                        <div>
                            <strong className={cn('g-shop-price', { 'g-highlight': hasMarketPrice })}>
                                {shopPriceExchangeDisplay}
                            </strong>
                        </div>

                        {hasMarketPrice && (<del className="g-market-price">{marketPriceExchangeDisplay}</del>)}
                    </div>
                    <Checkbox disabled={isSoldOut(item)} className={styles.checkbox} value={item.variantId}/>
                </div>
            </div>
        </div>
    )
}

const MODULE_ID = 'WEB buy together';

const ProductTogether: React.FC<IProps> = props => {
    const { productId, shownVariant } = props;
    const { variantId } = shownVariant;
    const together = useTogether({ productId, variantId, moduleId: MODULE_ID });
    const {
        wrapperRef,
        list: fullList,
        selected,
        setSelected,
        priceData,
        loading,
        calculateLoading,
        addToCart,
    } = together

    const { discountPriceExchange, productTotalPriceExchange, totalPriceExchange } = priceData || {}
    const hasDiscount = !!discountPriceExchange || (productTotalPriceExchange != totalPriceExchange)

    const [productDetailPopupProps, setProductDetailPopupProps] = useState<IProductDetailPopupProps>({
        visible: false,
        onClose: () => setProductDetailPopupProps({ ...productDetailPopupProps, visible: false })
    })

    const listRef = useRef<HTMLDivElement>(null);
    const list = fullList.length > 3 ? fullList.splice(0, 3) : fullList;
    const { t } = useTrans();

    useProductTagScale({
        listRef: listRef?.current,
        warpId: 'js-together-lists',
        monitorData: list, // 监听整个数据没有用list因为，（部分出现）商品之间切换路由时together有数据延迟更新 重新渲染了已经缩放后商品展示......
    });

    return (!shownVariant?.stockNum || list.length === 0) ? null : (
        <div
            ref={listRef}
            id={'js-together-lists'}
            className={styles.wrapper}
            data-t-mod={MODULE_ID}
        >
            <h2 className={styles.title}>{t('web_product_together_title')}</h2>
            <div className="flex flex-wrap wrapper" ref={wrapperRef}>
                <div className={cn(styles['product-list'], 'flex')}>
                    {/* 主商品 */}
                    <MainProductItem item={shownVariant}/>

                    <Checkbox.Group
                        className={cn(styles.list, 'flex flex-top')}
                        value={selected}
                        disabled={loading}
                        // @ts-ignore
                        onChange={e => setSelected(e)}
                    >
                        {list?.map((item: ITogetherProduct, index: number) => {
                            const prodTarget = `${shownVariant.variantTrackCode}.${item.track.variant || item.track.name}`;
                            return (
                                <Tracker key={item.variantId} data={{
                                    ...item,
                                    targetType: 'variant',
                                    target: prodTarget,
                                    relTarget: prodTarget,
                                    module: MODULE_ID,
                                    position: index + 1,
                                }}>
                                    <TogetherProductItem
                                        item={item}
                                        showLinkSymbol={index < (list.length - 1)}
                                        onQuickView={(variantId) => {
                                            setProductDetailPopupProps({
                                                ...productDetailPopupProps,
                                                visible: true,
                                                variantId,
                                            })
                                        }}
                                    />
                                </Tracker>
                            )
                        })}
                    </Checkbox.Group>
                </div>

                <div className={cn(styles['add-box'])}>
                    {hasDiscount && (
                        <ThemePrice
                            forceDisplay
                            className={styles.save}
                            value={priceData?.discountPriceExchange}
                            label={`${t('web_common_price_save')}: `}
                        />
                    )}

                    <div className={'flex'}>
                        <ThemePrice
                            forceDisplay
                            className={styles.total}
                            // value={totalPrice}
                            value={priceData?.totalPriceExchange}
                            label={`${t('web_summary_total')}: `}
                        />
                        {hasDiscount && (
                            <ThemePrice
                                forceDisplay
                                className={styles.del}
                                isDel
                                value={priceData?.productTotalPriceExchange}
                            />
                        )}

                    </div>

                    <Tracker data={{
                        targetType: 'other',
                        target: `${shownVariant.variantTrackCode}.add`,
                        relTarget: `${shownVariant.variantTrackCode}.add`,
                        module: 'WEB buy together',
                        position: 'add',
                    }}>
                        <div>
                            <Button
                                disabled={!selected.length}
                                type="primary"
                                loading={loading || calculateLoading}
                                onClick={addToCart}
                                className={styles['btn-add']}
                            >
                                {t('web_product_together_add_cart')}
                            </Button>
                        </div>
                    </Tracker>
                </div>
            </div>

            <ProductDetailPopup
                {...productDetailPopupProps}
                elements={['pictures', 'name', 'aboutItem', 'addCartForm', 'sku', 'spec', 'bundle', 'reviews', 'viewMore']}
            />
        </div>
    );
}

export default ProductTogether;
